import type { AudioManager } from '../audio/audioManager';

// Top-left audio control, docked to the RIGHT of the logo badge (same top +
// height) so the brand + sound controls read as one elegant cluster. Styled as
// a subtle blue glass that echoes the logo mark (was neutral white glass). Mute
// starts on; the first click both unmutes and resumes the AudioContext
// (first-gesture requirement).
//
// Visible in BOTH views — in dev it's the persistent quick-mute the inspector's
// buried AUDIO-tab checkbox never provided. External mutations (dev panel
// master slider) flow back through the poll loop so the UI stays in sync.
//
// Subtle blue glass echoing the logo mark; sits right of the logo, same height:
//   glass  rgba(74,106,170,0.20) bg · 1px rgba(150,178,228,0.45) border
//   shape  14px radius · blur(6px) · 6px 10px padding · 48px tall (= logo)
//   ink    #15233f deep navy · slider accent #5b86cf
const GLASS_BG = 'rgba(74, 106, 170, 0.20)';
const GLASS_BORDER = '1px solid rgba(150, 178, 228, 0.45)';
const INK = '#15233f';
const ACCENT_BLUE = '#5b86cf';

export function createAudioControls(audio: AudioManager): HTMLDivElement {
  const wrapper = document.createElement('div');
  wrapper.id = 'lec-audio-controls';
  wrapper.style.cssText = [
    'position: fixed',
    'top: 16px',                    // aligned with the logo's top
    'left: 110px',                  // flush right of the ~84px logo badge (16 + 84 + 10 gap)
    'height: 48px',                 // match the logo badge height — one cluster
    'display: flex',
    'align-items: center',
    'gap: 8px',
    'padding: 6px 10px',
    `background: ${GLASS_BG}`,
    `border: ${GLASS_BORDER}`,
    'border-radius: 14px',
    'backdrop-filter: blur(6px)',
    '-webkit-backdrop-filter: blur(6px)',
    `color: ${INK}`,
    'font: 12px/1.3 system-ui, sans-serif',
    'z-index: 1000',
    'user-select: none',
    'box-sizing: border-box',
    'transition: background 220ms ease',
  ].join('; ');

  const muteBtn = document.createElement('button');
  muteBtn.type = 'button';
  muteBtn.style.cssText = [
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'width: 30px',
    'height: 30px',
    'padding: 0',
    'background: none',
    'border: none',
    'border-radius: 999px',
    'cursor: pointer',
    'font-size: 16px',
    'line-height: 1',
    'touch-action: manipulation',
    'transition: transform 120ms ease, opacity 120ms ease',
    'opacity: 0.9',
  ].join('; ');
  const renderBtn = () => {
    muteBtn.textContent = audio.muted ? '🔇' : '🔊';
    muteBtn.title = audio.muted ? 'sound off — click to unmute' : 'sound on — click to mute';
  };
  renderBtn();
  muteBtn.addEventListener('pointerenter', () => { muteBtn.style.transform = 'scale(1.12)'; muteBtn.style.opacity = '1'; });
  muteBtn.addEventListener('pointerleave', () => { muteBtn.style.transform = ''; muteBtn.style.opacity = '0.9'; });
  muteBtn.addEventListener('click', async () => {
    try { await audio.resume(); } catch { /* ignored */ }
    // Auto-bump from near-zero so unmute actually produces sound.
    if (audio.muted && audio.getMasterVolume() < 0.05) {
      audio.setMasterVolume(0.7);
      slider.value = '0.7';
    }
    audio.setMuted(!audio.muted);
    renderBtn();
  });

  const slider = document.createElement('input');
  slider.type = 'range';
  slider.min = '0';
  slider.max = '1';
  slider.step = '0.01';
  slider.value = String(audio.getMasterVolume());
  slider.title = 'master volume';
  // Slim slider tinted brand-blue to match the glass.
  slider.style.cssText = `width: 88px; accent-color: ${ACCENT_BLUE}; cursor: pointer;`;
  slider.addEventListener('input', () => {
    audio.setMasterVolume(Number(slider.value));
  });

  wrapper.appendChild(muteBtn);
  wrapper.appendChild(slider);
  document.body.appendChild(wrapper);

  // Keep button + slider in sync if mute/volume change elsewhere (dev panel).
  setInterval(() => {
    renderBtn();
    const cur = String(audio.getMasterVolume());
    if (slider.value !== cur && document.activeElement !== slider) {
      slider.value = cur;
    }
  }, 200);

  return wrapper;
}
